<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import { IProfile, IShift, UpdateShiftAction } from '@/interfaces';
import { useWorkScheduleStore } from '@/stores/workSchedule';
import { useDepartmentStore } from '@/stores/department';
import { addDays, subDays } from 'date-fns';
import { useInstitutionStore } from '@/stores/institution';
import { get } from 'lodash';
import ReplaceShiftFlow from '@/components/workSchedule/ReplaceShiftFlow.vue';
import { eventBus } from '@/utils/eventBus';

interface Props {
  titleSuccess: string;
  title: string;
  shift: IShift;
  action: UpdateShiftAction;
}

const props = withDefaults(defineProps<Props>(), {});

const workScheduleStore = useWorkScheduleStore();
const departmentStore = useDepartmentStore();
const institutionStore = useInstitutionStore();
const showAuthFlow = ref<boolean>(false);
const showReplaceFlow = ref<boolean>(false);
const authFlowLoading = ref<boolean>(false);

const emit = defineEmits(['close']);

const shownDays = computed(() => {
  // If the department has checked overwrite institution setting it will be in the shift array.
  const matchingDepartment = (institutionStore.settings?.shift || []).find(
    ({ departmentId }) =>
      departmentId?.toString() == departmentStore.id.toString()
  );

  return (
    (get(
      matchingDepartment,
      'daysForward',
      departmentStore.settings?.shift?.daysForward
    ) || 1) - 1 // Subtract 1 because the core admin setting is not 0 based but 1 should mean 1 days forward.
  );
});

const dateInterval = computed(() => {
  const today = new Date();

  return {
    from: subDays(today, shownDays.value - 1),
    to: addDays(today, shownDays.value - 1),
  };
});

onMounted(() => {
  if (props.action === 'replace') showReplaceFlow.value = true;
  if (props.action !== 'replace') showAuthFlow.value = true;
});

const closeFlow = () => {
  showAuthFlow.value = false;
  showReplaceFlow.value = false;
  emit('close');
};

const cancelShift = async () => {
  await workScheduleStore.shiftUpdate({
    shiftId: props.shift.id,
    dates: dateInterval.value,
    cancel: true,
  });
};

const reactivateShift = async () => {
  await workScheduleStore.shiftUpdate({
    shiftId: props.shift.id,
    dates: dateInterval.value,
    cancel: false,
  });
};

const replaceShift = async (employee: IProfile) => {
  try {
    authFlowLoading.value = true;

    workScheduleStore.setReplaceWithEmployeeId(employee.id);

    await workScheduleStore.shiftUpdate({
      shiftId: props.shift.id,
      dates: dateInterval.value,
      cancel: false,
    });
    eventBus.emit('success', props.titleSuccess);
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showReplaceFlow.value = false;
    showAuthFlow.value = false;
    workScheduleStore.setReplaceWithEmployeeId(null);
  }
};

const deleteShift = async () => {
  await workScheduleStore.shiftDelete({
    shiftId: props.shift.id,
    dates: dateInterval.value,
  });
};

const onSuccess = async (payload: IProfile) => {
  try {
    authFlowLoading.value = true;

    if (props.action === 'cancel') await cancelShift();
    if (props.action === 'reactivate') await reactivateShift();
    if (props.action === 'replace') await replaceShift(payload);
    if (props.action === 'delete') await deleteShift();
    eventBus.emit('success', props.titleSuccess);
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showReplaceFlow.value = false;
    showAuthFlow.value = false;
    workScheduleStore.setReplaceWithEmployeeId(null);
  }
};
</script>

<template>
  <AuthFlow
    v-if="showAuthFlow"
    :title="title"
    :employee-only="true"
    :loading="authFlowLoading"
    @close="closeFlow"
    @authenticated="onSuccess"
  />

  <ReplaceShiftFlow
    v-if="showReplaceFlow"
    :title="title"
    :shift="shift"
    :loading="authFlowLoading"
    @close="closeFlow"
    @success="replaceShift"
  />
</template>
